// src/pages/Account.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk, useAuth } from '@clerk/clerk-react';
import AppNav from '../components/AppNav';
import ProfilePhotoControl from '../components/ProfilePhotoControl';
import ProfileTaglineControl from '../components/ProfileTaglineControl';
import { HomeScreenCard } from '../components/AddToHomeScreen';
import { PushNotificationToggle } from '../components/PushNotificationPrompt';
import { fetchPrograms, fetchSiteContent, generateSlug } from '../services/firebase';
import MOCK_PROGRAMS from '../data/mockPrograms';
import { useAnnualOffer } from '../components/PlanPicker';
import { resolvePlanCopy, fillTemplate, formatMoney } from '../services/subscriptionPlans';
import ClassDayReminderControl from '../components/ClassDayReminderControl';

// ── Format a user's locked-in subscription price for display (SUB-002) ──
// Reads the price snapshot stored in Clerk publicMetadata at subscription
// creation time (api/stripe-webhook.js writes amount/currency/interval).
// Returns null when any input is missing or malformed so callers can fall
// through to the public siteConfig.subscriptionPriceLabel.
//
// This is the visual grandfathering mechanism: existing subscribers see
// their own locked-in price on the Account card / Resume confirm dialog
// instead of whatever the current public Display Price happens to be.
// Whole-dollar amounts render as "$15/mo"; with cents as "$15.99/mo".
function formatUserPrice(amount, currency, interval) {
    if (typeof amount !== 'number' || amount < 0 || !isFinite(amount)) return null;
    if (!currency || typeof currency !== 'string') return null;
    const intervalShortMap = {
        month: '/mo',
        year: '/yr',
        week: '/wk',
        day: '/day',
    };
    const intervalShort = intervalShortMap[interval];
    if (!intervalShort) return null;
    let formatted;
    try {
        formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
            minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        }).format(amount / 100);
    } catch (e) {
        return null;
    }
    return `${formatted}${intervalShort}`;
}

function Account({ demoMode }) {
    const navigate = useNavigate();
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingName, setEditingName] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [nameSaving, setNameSaving] = useState(false);
    const [passwordMode, setPasswordMode] = useState(false);
    // Email change is two steps: enter the new address (Clerk mails a code),
    // then enter the code. `emailStep` is 'enter' | 'verify'.
    const [emailMode, setEmailMode] = useState(false);
    const [emailStep, setEmailStep] = useState('enter');
    const [newEmail, setNewEmail] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [pendingEmailObj, setPendingEmailObj] = useState(null);
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailNotice, setEmailNotice] = useState('');
    const [emailSuccess, setEmailSuccess] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [cancelError, setCancelError] = useState('');
    const [cancelSuccess, setCancelSuccess] = useState(false);
    // Captured from the cancel-subscription API response so the success message can
    // show the specific end-of-access date instead of vague "end of billing period"
    // copy. Falls through to the generic fallback when Stripe omits
    // current_period_end (rare). Becomes irrelevant after clerkUser.reload() because
    // the UI then flips to the canonical Resume branch which displays the same date
    // from Clerk metadata.
    const [cancelAt, setCancelAt] = useState('');
    // On-brand cancellation modal (replaces window.confirm). Captures a REQUIRED
    // reason — one of five, the last being an explicit opt-out — which is stored
    // in Firestore (cancellationFeedback) for future in-app reporting. Modal copy
    // + reason labels are admin-editable via siteConfig with inline fallbacks.
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    // M-3: resume-subscription state — symmetric with cancel state above
    const [resumeLoading, setResumeLoading] = useState(false);
    const [resumeError, setResumeError] = useState('');
    const [resumeSuccess, setResumeSuccess] = useState(false);
    // Stripe Billing Customer Portal (payment method + invoices). Independent of
    // cancel/resume — opening the portal never touches subscription state here.
    const [billingLoading, setBillingLoading] = useState(false);
    const [billingError, setBillingError] = useState('');
    const [c, setC] = useState({});

    let userName = '';
    let userEmail = '';
    let purchasedIds = [];
    let signOut = () => { navigate('/'); };
    let clerkUser = null;
    let hasPasswordAuth = false;
    let hasSubscription = false;
    let subscriptionStarted = '';
    let subscriptionCancelAt = '';
    // SUB-002: per-user price snapshot captured at subscription creation time.
    // Null for the launch cohort until the backfill script runs. Account card
    // and Resume confirm fall back to siteConfig.subscriptionPriceLabel when
    // any of these three values is missing.
    let subscriptionPriceAmount = null;
    let subscriptionPriceCurrency = null;
    let subscriptionPriceInterval = null;
    // Founding members who locked in a year: their founding monthly rate (cents).
    let subscriptionFoundingAmount = null;
    let getToken = async () => null; // fallback in demo mode / Clerk unavailable

    if (!demoMode) {
        try {
            const { user } = useUser();
            const clerk = useClerk();
            const auth = useAuth(); // H-5: use the hook instead of window.Clerk global
            clerkUser = user;
            userName = user?.fullName || user?.firstName || '';
            userEmail = user?.primaryEmailAddress?.emailAddress || '';
            purchasedIds = user?.publicMetadata?.purchasedPrograms || [];
            hasSubscription = user?.publicMetadata?.classSubscription === true;
            subscriptionStarted = user?.publicMetadata?.classSubscriptionStarted || '';
            subscriptionCancelAt = user?.publicMetadata?.classSubscriptionCancelAt || '';
            subscriptionPriceAmount = typeof user?.publicMetadata?.classSubscriptionPriceAmount === 'number'
                ? user.publicMetadata.classSubscriptionPriceAmount
                : null;
            subscriptionPriceCurrency = user?.publicMetadata?.classSubscriptionPriceCurrency || null;
            subscriptionPriceInterval = user?.publicMetadata?.classSubscriptionPriceInterval || null;
            subscriptionFoundingAmount = typeof user?.publicMetadata?.classSubscriptionFoundingAmount === 'number'
                ? user.publicMetadata.classSubscriptionFoundingAmount
                : null;
            signOut = () => clerk.signOut().then(() => navigate('/'));
            hasPasswordAuth = (user?.passwordEnabled) || false;
            getToken = auth.getToken;
        } catch (e) { }
    } else {
        userName = 'Danielle Demo';
        userEmail = 'danielle@example.com';
        purchasedIds = ['mock-1'];
    }

    // Step 1 — attach the new address to the Clerk user and ask Clerk to email a
    // verification code. Nothing about the account changes yet: at this point the
    // member simply has a second, unverified address.
    const startEmailChange = async () => {
        setEmailError(''); setEmailNotice('');
        const candidate = newEmail.trim();
        if (!candidate || !candidate.includes('@')) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        if (candidate.toLowerCase() === (userEmail || '').toLowerCase()) {
            setEmailError('That is already your email address.');
            return;
        }
        if (!clerkUser) return;
        setEmailSaving(true);
        try {
            const created = await clerkUser.createEmailAddress({ email: candidate });
            await created.prepareVerification({ strategy: 'email_code' });
            setPendingEmailObj(created);
            setEmailStep('verify');
            setEmailNotice(`We sent a code to ${candidate}.`);
        } catch (e) {
            setEmailError(e?.errors?.[0]?.longMessage || e?.message || 'Could not start the change. Please try again.');
        }
        setEmailSaving(false);
    };

    // Step 2 — verify the code, then hand off to the server to promote it to
    // primary and sync Stripe + Flodesk. The server re-checks verification
    // itself; this call is a convenience, not the security boundary.
    const confirmEmailChange = async () => {
        setEmailError(''); setEmailNotice('');
        if (!pendingEmailObj || !emailCode.trim()) {
            setEmailError('Enter the code we emailed you.');
            return;
        }
        setEmailSaving(true);
        try {
            await pendingEmailObj.attemptVerification({ code: emailCode.trim() });

            const token = typeof getToken === 'function' ? await getToken() : null;
            const res = await fetch('/api/change-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ emailAddressId: pendingEmailObj.id }),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data?.success) {
                throw new Error(data?.error || 'Could not finish the change. Please try again.');
            }

            // Reload so the header, avatar initial and any email-derived UI pick
            // up the new primary address without a manual refresh.
            if (clerkUser?.reload) { try { await clerkUser.reload(); } catch (_) { /* cosmetic */ } }

            // Surface a partial sync rather than claiming a clean success.
            const failed = Object.entries(data.steps || {})
                .filter(([, v]) => v && v.ok === false)
                .map(([k]) => k);
            setEmailSuccess(true);
            setEmailNotice(failed.length
                ? 'Your email is updated. Some billing details may take a moment to sync — Danielle has been notified if anything needs a hand.'
                : '');
            setTimeout(() => {
                setEmailMode(false); setEmailSuccess(false); setEmailStep('enter');
                setNewEmail(''); setEmailCode(''); setPendingEmailObj(null); setEmailNotice('');
            }, 2600);
        } catch (e) {
            setEmailError(e?.errors?.[0]?.longMessage || e?.message || 'That code did not work. Please try again.');
        }
        setEmailSaving(false);
    };

    // Abandoning midway leaves an unverified address attached to the account.
    // Clean it up so a retry doesn't collide with it.
    const cancelEmailChange = async () => {
        const pending = pendingEmailObj;
        setEmailMode(false); setEmailStep('enter'); setNewEmail(''); setEmailCode('');
        setPendingEmailObj(null); setEmailError(''); setEmailNotice('');
        if (pending?.destroy) { try { await pending.destroy(); } catch (_) { /* best-effort */ } }
    };

    useEffect(() => {
        fetchPrograms().then((p) => {
            setPrograms((p || MOCK_PROGRAMS).filter((prog) => prog.active !== false));
            setLoading(false);
        });
        fetchSiteContent().then((content) => setC(content || {}));
    }, []);

    const purchasedPrograms = programs.filter((p) => purchasedIds.includes(p.id));

    // SUB-002: derive the display price for THIS user. Per-user actual price
    // wins when stored at signup; falls back to public Display Price for the
    // launch cohort until backfill runs, then to '$15/mo' if siteConfig hasn't
    // loaded yet. This single value drives both the subscription card label
    // and the Resume confirm dialog so they stay in sync.
    const userActualPrice = formatUserPrice(subscriptionPriceAmount, subscriptionPriceCurrency, subscriptionPriceInterval);
    const displaySubscriptionPrice = userActualPrice || c.subscriptionPriceLabel || '$15/mo';

    // ── Switch to yearly (docs/ux/annual-plan.md) ──
    // Client-side this is only a HINT for whether to show the row: the member's
    // own price snapshot must equal today's monthly price and they must not be
    // cancelling. $15-for-life members snapshot 1500 ≠ 1900, so they never see
    // it — and api/switch-to-yearly.js re-checks the real Stripe price (and any
    // discount) before anything is charged. Flag off ⇒ nothing renders.
    const switchOffer = useAnnualOffer(!demoMode && c.enableAnnualPlan === true && hasSubscription
        && !subscriptionCancelAt && subscriptionPriceInterval === 'month');
    const switchCurrencyOk = !!switchOffer
        && (subscriptionPriceCurrency || '').toLowerCase() === switchOffer.monthly.currency;
    // Founding members (2026-09-27) pay LESS than today's monthly price. They may
    // lock in a year too; the server re-checks this against the real Stripe price.
    const isFoundingMonthly = switchCurrencyOk && typeof subscriptionPriceAmount === 'number'
        && subscriptionPriceAmount > 0 && subscriptionPriceAmount < switchOffer.monthly.amount;
    const canSwitchToYearly = switchCurrencyOk
        && (subscriptionPriceAmount === switchOffer.monthly.amount || isFoundingMonthly);
    const planCopy = resolvePlanCopy(c);
    const [switchOpen, setSwitchOpen] = useState(false);
    const [switchPreview, setSwitchPreview] = useState(null);
    const [switchLoading, setSwitchLoading] = useState(false);
    const [switchError, setSwitchError] = useState('');
    const [switchDone, setSwitchDone] = useState(false);
    const [switchNote, setSwitchNote] = useState('');

    // ── Founding members on yearly: "go back to $15/month when my year ends" ──
    // Deliberately NOT gated on the admin yearly-plan flag — a founder's right
    // to return to their rate must not disappear if yearly is switched off.
    const isYearlyFounder = !demoMode && hasSubscription && subscriptionPriceInterval === 'year'
        && typeof subscriptionFoundingAmount === 'number' && subscriptionFoundingAmount > 0;
    const [foundingStatus, setFoundingStatus] = useState(null);
    const [foundingConfirm, setFoundingConfirm] = useState(false);
    const [foundingBusy, setFoundingBusy] = useState(false);
    const [foundingError, setFoundingError] = useState('');

    const callFounding = async (action) => {
        const token = await getToken();
        const resp = await fetch('/api/founding-rate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ action }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
        return data;
    };

    useEffect(() => {
        if (!isYearlyFounder || subscriptionCancelAt) { setFoundingStatus(null); return undefined; }
        let live = true;
        callFounding('status')
            .then((d) => { if (live) setFoundingStatus(d && d.founder ? d : null); })
            .catch(() => { if (live) setFoundingStatus(null); });
        return () => { live = false; };
        // callFounding reads getToken live; re-run only when eligibility changes.
    }, [isYearlyFounder, subscriptionCancelAt]);

    const changeFounding = async (action) => {
        setFoundingBusy(true);
        setFoundingError('');
        try {
            const d = await callFounding(action);
            if (d && d.founder) setFoundingStatus(d);
            setFoundingConfirm(false);
        } catch (e) {
            setFoundingError(e.message || 'Something went wrong. Please try again.');
        }
        setFoundingBusy(false);
    };

    const callSwitch = async (body) => {
        const token = await getToken();
        const resp = await fetch('/api/switch-to-yearly', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify(body),
        });
        const data = await resp.json().catch(() => ({}));
        return { ok: resp.ok, data };
    };

    // Ask Stripe what switching would cost today. Nothing is charged here.
    const openSwitch = async () => {
        setSwitchOpen(true);
        setSwitchPreview(null);
        setSwitchError('');
        setSwitchLoading(true);
        try {
            const { ok, data } = await callSwitch({ action: 'preview' });
            if (!ok) throw new Error(data.error || 'Could not check the price. Please try again.');
            if (!data.eligible) {
                throw new Error(c.switchYearlyIneligible || "This membership can't be switched online. Reach out and Danielle will help.");
            }
            setSwitchPreview(data);
        } catch (e) {
            setSwitchError(e.message || 'Something went wrong. Please try again.');
        }
        setSwitchLoading(false);
    };

    const confirmSwitch = async () => {
        if (!switchPreview || switchLoading) return;
        setSwitchLoading(true);
        setSwitchError('');
        try {
            const { ok, data } = await callSwitch({ action: 'confirm', prorationDate: switchPreview.prorationDate });
            if (!ok || !data.switched) throw new Error(data.error || 'Something went wrong. Please try again.');
            setSwitchDone(true);
            setSwitchNote(data.snapshotSaved ? '' : 'Your billing is updated — the price shown here may take a moment to catch up.');
            setSwitchOpen(false);
            // Pull the new price snapshot so the card reads the yearly price.
            if (clerkUser?.reload) { try { await clerkUser.reload(); } catch (_) { } }
        } catch (e) {
            setSwitchError(e.message || 'Something went wrong. Please try again.');
        }
        setSwitchLoading(false);
    };

    const initials = userName
        ? userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
        : (userEmail ? userEmail[0].toUpperCase() : '?');

    const cardStyle = {
        background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
        borderRadius: 24,
        border: '1px solid rgba(255,255,255,0.55)',
        boxShadow: 'var(--shadow-md)',
        position: 'relative',
        overflow: 'hidden',
    };

    const specHighlight = {
        position: 'absolute', top: 0, left: 0, right: 0, height: '50%',
        background: 'radial-gradient(ellipse 70% 80% at 25% 0%, rgba(255,255,255,0.28) 0%, transparent 60%)',
        pointerEvents: 'none', zIndex: 0, borderRadius: '24px 24px 0 0',
    };

    // Cancellation reasons. `key` is stable for reporting; `label` is the
    // displayed text, admin-editable via siteConfig with the fallbacks below.
    const cancelReasons = [
        { key: 'too_expensive', label: c.cancelReason1 || "It's too expensive right now" },
        { key: 'not_using', label: c.cancelReason2 || "I'm not using it enough" },
        { key: 'not_right_fit', label: c.cancelReason3 || "The classes weren't the right fit for me" },
        { key: 'taking_break', label: c.cancelReason4 || "I'm taking a break / life got busy" },
        { key: 'no_specify', label: c.cancelReason5 || "I'd rather not specify" },
    ];

    // The actual cancellation. Behaviorally identical to the prior inline
    // onClick (same endpoint, response handling, and clerkUser.reload flip) —
    // the ONLY additions are the required reason fields in the body and closing
    // the modal on success. A reason is required, so this no-ops without one.
    const doCancel = async () => {
        if (!cancelReason) return;
        setCancelLoading(true);
        setCancelError('');
        try {
            const token = await getToken();
            const selected = cancelReasons.find((r) => r.key === cancelReason);
            const resp = await fetch('/api/cancel-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    userId: clerkUser?.id,
                    reason: cancelReason,
                    reasonLabel: selected ? selected.label : '',
                }),
            });
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                throw new Error(errData.error || 'Failed to cancel subscription');
            }
            // Capture the end-of-access date for the success message (defensive:
            // may be omitted if Stripe returned no current_period_end).
            const data = await resp.json().catch(() => ({}));
            if (data && data.cancelAt) setCancelAt(data.cancelAt);
            setShowCancelModal(false);
            setCancelSuccess(true);
            // Pull fresh Clerk metadata so the card flips to the canonical
            // "Cancels [date]" + Resume state without a manual refresh. Wrapped
            // in try/catch — failure leaves cancelSuccess + cancelAt as fallback.
            if (clerkUser?.reload) {
                try { await clerkUser.reload(); } catch (_) { }
            }
        } catch (e) {
            setCancelError(e.message || 'Something went wrong. Please try again.');
        }
        setCancelLoading(false);
    };

    // Open the Stripe Billing Customer Portal (payment method + invoices). Mints
    // a portal session server-side, then redirects the whole tab to Stripe; the
    // portal's return_url brings them back to /account. Read-only w.r.t. the
    // app's subscription state — see api/billing-portal.js.
    const openBillingPortal = async () => {
        setBillingLoading(true);
        setBillingError('');
        try {
            const token = await getToken();
            const resp = await fetch('/api/billing-portal', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ userId: clerkUser?.id }),
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || !data.url) {
                throw new Error(data.error || 'Could not open billing portal');
            }
            window.location.href = data.url;
            // Intentionally leave billingLoading true — the tab is navigating away.
        } catch (e) {
            setBillingError(e.message || 'Something went wrong. Please try again.');
            setBillingLoading(false);
        }
    };

    return (
        <div className="mom-page-shell" style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <AppNav demoMode={demoMode} />
            <div style={{ maxWidth: 480, margin: '0 auto', padding: '48px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* ── Profile Card ──────────────────────────── */}
                <div className="slide-up" style={{ ...cardStyle, padding: '40px 32px', textAlign: 'center' }}>
                    <div style={specHighlight} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        {/* Profile photo control (docs/ux/profile-photo.md). Demo mode /
                            no Clerk user keeps the original static initial circle. */}
                        {!demoMode && clerkUser ? (
                            <ProfilePhotoControl clerkUser={clerkUser} initials={initials} content={c} />
                        ) : (
                        <div style={{
                            width: 72, height: 72, borderRadius: '50%',
                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 4px 16px color-mix(in srgb, var(--mom-primary) 25%, transparent), inset 0 1px 0 rgba(255,255,255,0.2)',
                        }}>
                            <span style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 26, fontWeight: 700, color: 'var(--mom-white)', fontStyle: 'italic' }}>{initials}</span>
                        </div>
                        )}

                        {editingName ? (
                            <div style={{ marginBottom: 8 }}>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 10, justifyContent: 'center' }}>
                                    <input type="text" className="mom-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" style={{ fontSize: 14, textAlign: 'center', maxWidth: 140 }} />
                                    <input type="text" className="mom-input" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" style={{ fontSize: 14, textAlign: 'center', maxWidth: 140 }} />
                                </div>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <button className="mom-btn mom-btn-primary" disabled={nameSaving} onClick={async () => {
                                        if (!clerkUser) return;
                                        setNameSaving(true);
                                        try { await clerkUser.update({ firstName: firstName.trim(), lastName: lastName.trim() }); }
                                        catch (e) { }
                                        setNameSaving(false);
                                        setEditingName(false);
                                    }} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 50, opacity: nameSaving ? 0.5 : 1 }}>
                                        {nameSaving ? 'Saving...' : 'Save'}
                                    </button>
                                    <button className="mom-btn mom-btn-ghost" onClick={() => setEditingName(false)} style={{ fontSize: 13, color: 'var(--mom-warm-gray)' }}>Cancel</button>
                                </div>
                            </div>
                        ) : emailMode ? (
                            <div style={{ marginTop: 4, textAlign: 'left', maxWidth: 300, margin: '4px auto 0' }}>
                                {emailStep === 'enter' ? (
                                    <>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginBottom: 8, textAlign: 'center', lineHeight: 1.5 }}>
                                            You'll sign in with the new address. Your password and everything you've completed stay exactly as they are.
                                        </p>
                                        <input
                                            type="email" className="mom-input" value={newEmail}
                                            onChange={(e) => setNewEmail(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') startEmailChange(); }}
                                            placeholder="New email address" autoComplete="email"
                                            style={{ fontSize: 14, marginBottom: 12 }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginBottom: 8, textAlign: 'center', lineHeight: 1.5 }}>
                                            Enter the 6-digit code we emailed you.
                                        </p>
                                        <input
                                            type="text" inputMode="numeric" className="mom-input" value={emailCode}
                                            onChange={(e) => setEmailCode(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') confirmEmailChange(); }}
                                            placeholder="000000" autoComplete="one-time-code" maxLength={8}
                                            style={{ fontSize: 16, marginBottom: 12, letterSpacing: '0.18em', textAlign: 'center' }}
                                        />
                                    </>
                                )}
                                {emailError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #e53e3e)', marginBottom: 8, textAlign: 'center' }}>{emailError}</p>}
                                {emailNotice && !emailError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', marginBottom: 8, textAlign: 'center', lineHeight: 1.5 }}>{emailNotice}</p>}
                                {emailSuccess && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-success)', marginBottom: 8, textAlign: 'center' }}>Email updated!</p>}
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <button
                                        className="mom-btn mom-btn-primary" disabled={emailSaving}
                                        onClick={emailStep === 'enter' ? startEmailChange : confirmEmailChange}
                                        style={{ padding: '8px 20px', fontSize: 13, borderRadius: 50, opacity: emailSaving ? 0.5 : 1 }}
                                    >
                                        {emailSaving
                                            ? (emailStep === 'enter' ? 'Sending...' : 'Saving...')
                                            : (emailStep === 'enter' ? 'Send code' : 'Confirm')}
                                    </button>
                                    <button className="mom-btn mom-btn-ghost" onClick={cancelEmailChange} style={{ fontSize: 13, color: 'var(--mom-warm-gray)' }}>Cancel</button>
                                </div>
                            </div>
                        ) : passwordMode ? (
                            <div style={{ marginTop: 4, textAlign: 'left', maxWidth: 300, margin: '4px auto 0' }}>
                                {hasPasswordAuth && (
                                    <input type="password" className="mom-input" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current password" style={{ fontSize: 14, marginBottom: 8 }} />
                                )}
                                <input type="password" className="mom-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" style={{ fontSize: 14, marginBottom: 8 }} />
                                <input type="password" className="mom-input" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" style={{ fontSize: 14, marginBottom: 12 }} />
                                {pwError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #e53e3e)', marginBottom: 8, textAlign: 'center' }}>{pwError}</p>}
                                {pwSuccess && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-success)', marginBottom: 8, textAlign: 'center' }}>Password updated!</p>}
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <button className="mom-btn mom-btn-primary" disabled={pwSaving} onClick={async () => {
                                        setPwError(''); setPwSuccess(false);
                                        if (newPassword.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
                                        if (newPassword !== confirmPassword) { setPwError('Passwords do not match.'); return; }
                                        if (!clerkUser) return;
                                        setPwSaving(true);
                                        try {
                                            await clerkUser.updatePassword({
                                                ...(hasPasswordAuth ? { currentPassword } : {}),
                                                newPassword,
                                            });
                                            setPwSuccess(true);
                                            setTimeout(() => { setPasswordMode(false); setPwSuccess(false); }, 1500);
                                        } catch (e) {
                                            setPwError(e?.errors?.[0]?.longMessage || e?.message || 'Could not update password. Please try again.');
                                        }
                                        setPwSaving(false);
                                    }} style={{ padding: '8px 20px', fontSize: 13, borderRadius: 50, opacity: pwSaving ? 0.5 : 1 }}>
                                        {pwSaving ? 'Saving...' : 'Update Password'}
                                    </button>
                                    <button className="mom-btn mom-btn-ghost" onClick={() => { setPasswordMode(false); setPwError(''); }} style={{ fontSize: 13, color: 'var(--mom-warm-gray)' }}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {userName && (
                                    <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 6 }}>{userName}</h1>
                                )}
                                {/* Optional one-liner for the member card (PHOTO-002). Inline edit — no overlay. */}
                                {!demoMode && clerkUser && (
                                    <ProfileTaglineControl userId={clerkUser.id} content={c} />
                                )}
                                {userEmail && (
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 12 }}>{userEmail}</p>
                                )}
                                {!demoMode && (
                                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => {
                                            setFirstName(clerkUser?.firstName || '');
                                            setLastName(clerkUser?.lastName || '');
                                            setEditingName(true);
                                        }} style={{ fontSize: 12, color: 'var(--mom-link)', padding: '4px 0' }}>
                                            Edit name
                                        </button>
                                        <span style={{ color: 'var(--mom-cream-dark)', fontSize: 12 }}>·</span>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => { setEmailMode(true); setEmailStep('enter'); setEmailError(''); setEmailNotice(''); setEmailSuccess(false); setNewEmail(''); setEmailCode(''); setPendingEmailObj(null); }} style={{ fontSize: 12, color: 'var(--mom-link)', padding: '4px 0' }}>
                                            Change email
                                        </button>
                                        <span style={{ color: 'var(--mom-cream-dark)', fontSize: 12 }}>·</span>
                                        <button className="mom-btn mom-btn-ghost" onClick={() => { setPasswordMode(true); setPwError(''); setPwSuccess(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} style={{ fontSize: 12, color: 'var(--mom-link)', padding: '4px 0' }}>
                                            {hasPasswordAuth ? 'Change password' : 'Set password'}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* ── The Membership ────────────────────────── */}
                {/* Shown to anyone without an active subscription so the membership is
                    one tap away from Account. Comped friends already have full access,
                    so they never see it; admin does, so Danielle can preview her copy.
                    /join handles signed-in checkout + plan choice. */}
                {!demoMode && !hasSubscription && clerkUser?.publicMetadata?.role !== 'friend' && (
                    <div className="slide-up" style={{ ...cardStyle, padding: '28px 28px', animationDelay: '0.08s' }}>
                        <div style={specHighlight} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>{c.accountMembershipHeading || 'The Membership'}</h2>
                            <div className="mom-inset" style={{ padding: '20px', textAlign: 'center' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 12 }}>{c.accountMembershipDescription || 'Live classes, the full On Demand library and challenges — all in one membership.'}</p>
                                <button className="mom-btn mom-btn-primary" onClick={() => navigate('/join')}
                                    style={{ padding: '10px 28px', fontSize: 13, borderRadius: 50 }}>
                                    {c.accountMembershipButtonLabel || 'Join Membership'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Subscription ─────────────────────────── */}
                {!demoMode && hasSubscription && (
                    <div className="slide-up" style={{ ...cardStyle, padding: '28px 28px', animationDelay: '0.08s' }}>
                        <div style={specHighlight} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>Subscription</h2>

                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 14,
                                padding: '14px 18px', borderRadius: 14,
                                background: 'rgba(237,240,244,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                                boxShadow: 'inset 0 2px 4px rgba(0,10,40,0.06), inset 0 1px 1px rgba(0,10,40,0.04), 0 1px 0 rgba(255,255,255,0.60)',
                                marginBottom: 14,
                            }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                    background: subscriptionCancelAt ? 'linear-gradient(135deg, var(--mom-warm-gray), #999)' : 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: subscriptionCancelAt ? 'none' : '0 2px 8px color-mix(in srgb, var(--mom-primary) 20%, transparent)',
                                }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z" />
                                    </svg>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 2 }}>
                                        {c.accountSubscriptionLabel || 'Unlimited Classes'} — {displaySubscriptionPrice}
                                    </p>
                                    {subscriptionCancelAt ? (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>
                                            Cancels {new Date(subscriptionCancelAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                        </p>
                                    ) : subscriptionStarted ? (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>
                                            Member since {new Date(subscriptionStarted).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                        </p>
                                    ) : (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>Active</p>
                                    )}
                                </div>
                            </div>

                            {/* Manage billing — Stripe Customer Portal (payment method + invoices).
                                Shown for all active subscribers, independent of cancel state. */}
                            <button disabled={billingLoading} onClick={openBillingPortal} style={{
                                display: 'block', width: '100%', padding: '11px 20px', borderRadius: 14, fontSize: 13,
                                fontFamily: 'var(--mom-font-sans)', fontWeight: 600, color: 'var(--mom-charcoal)',
                                background: 'var(--mom-white)', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer',
                                marginBottom: 14, opacity: billingLoading ? 0.6 : 1,
                                boxShadow: '0 1px 2px rgba(0,10,40,0.04)',
                            }}>
                                {billingLoading ? 'Opening…' : (c.accountManageBillingLabel || 'Manage billing & payment method')}
                            </button>
                            {billingError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #e53e3e)', marginBottom: 12, textAlign: 'center' }}>{billingError}</p>}

                            {/* Class-day reminder (release-day classes) — one opt-in, her own
                                time. Renders nothing until her saved setting has loaded. */}
                            {clerkUser?.id && (
                                <div style={{ marginBottom: 14 }}>
                                    <ClassDayReminderControl userId={clerkUser.id} getToken={getToken} copy={c} variant="account" />
                                </div>
                            )}

                            {/* Switch to yearly — only for members on the current monthly price
                                (never the $15-for-life cohort), not cancelling, flag on. */}
                            {canSwitchToYearly && !switchDone && (
                                <button type="button" onClick={openSwitch} style={{
                                    display: 'block', width: '100%', padding: '11px 20px', borderRadius: 14, fontSize: 13,
                                    fontFamily: 'var(--mom-font-sans)', fontWeight: 700, color: 'var(--mom-primary-dark)',
                                    background: 'color-mix(in srgb, var(--mom-primary) 8%, var(--mom-white, #fff))',
                                    border: '1.5px solid var(--mom-primary)', cursor: 'pointer', marginBottom: 14,
                                }}>
                                    {isFoundingMonthly
                                        ? `${c.foundingLockLabel || 'Lock in a year'} · ${switchOffer.annualPrice}`
                                        : `${planCopy.switchLabel} · ${fillTemplate(planCopy.savingsTemplate, { amount: switchOffer.savingsPlain })}`}
                                </button>
                            )}
                            {switchDone && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)', fontWeight: 600, textAlign: 'center', lineHeight: 1.5, marginBottom: 14 }}>
                                    {c.switchYearlySuccess || "You're on the yearly plan. Thank you for being here!"}
                                    {switchNote && <span style={{ display: 'block', fontWeight: 400, fontSize: 12, marginTop: 4 }}>{switchNote}</span>}
                                </p>
                            )}

                            {/* Founding member on yearly — self-serve way back to their rate. */}
                            {foundingStatus && foundingStatus.founder && !foundingStatus.cancelling && (() => {
                                const tokens = {
                                    rate: formatMoney(foundingStatus.amount, foundingStatus.currency),
                                    yearly: displaySubscriptionPrice,
                                    date: foundingStatus.switchesOn
                                        ? new Date(foundingStatus.switchesOn).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                                        : 'your renewal date',
                                };
                                const quiet = { fontFamily: 'var(--mom-font-sans)', fontSize: 12.5, color: 'var(--mom-charcoal)', lineHeight: 1.55, textAlign: 'center' };
                                const outlineBtn = {
                                    display: 'block', width: '100%', padding: '11px 20px', borderRadius: 14, fontSize: 13,
                                    fontFamily: 'var(--mom-font-sans)', fontWeight: 600, color: 'var(--mom-charcoal)',
                                    background: 'var(--mom-white)', border: '1px solid var(--mom-cream-dark)',
                                    cursor: foundingBusy ? 'default' : 'pointer', opacity: foundingBusy ? 0.6 : 1,
                                };
                                return (
                                    <div style={{ marginBottom: 14 }}>
                                        {foundingStatus.scheduled ? (
                                            <>
                                                <p style={{ ...quiet, fontWeight: 600, marginBottom: 8 }}>
                                                    {fillTemplate(c.foundingScheduledTemplate || 'Switching to your founding rate of {rate}/month on {date}.', tokens)}
                                                </p>
                                                <button type="button" disabled={foundingBusy} onClick={() => changeFounding('unschedule')} style={outlineBtn}>
                                                    {foundingBusy ? 'Saving…' : (c.foundingUndoLabel || 'Keep yearly instead')}
                                                </button>
                                            </>
                                        ) : foundingConfirm ? (
                                            <>
                                                <p style={{ ...quiet, marginBottom: 10 }}>
                                                    {fillTemplate(c.foundingBackConfirm || "From {date} you'll pay {rate}/month instead of {yearly}. Nothing changes before then, and you can undo this any time before {date}.", tokens)}
                                                </p>
                                                <button type="button" disabled={foundingBusy} onClick={() => changeFounding('schedule')} style={{
                                                    ...outlineBtn, marginBottom: 8, fontWeight: 700, color: 'var(--mom-primary-dark)',
                                                    border: '1.5px solid var(--mom-primary)',
                                                    background: 'color-mix(in srgb, var(--mom-primary) 8%, var(--mom-white, #fff))',
                                                }}>
                                                    {foundingBusy ? 'Saving…' : (c.foundingBackConfirmButton || 'Yes, switch me back then')}
                                                </button>
                                                <button type="button" disabled={foundingBusy} onClick={() => setFoundingConfirm(false)} style={{ ...outlineBtn, border: 'none', background: 'none' }}>
                                                    {c.foundingKeepYearlyLabel || 'Keep yearly'}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <p style={{ ...quiet, marginBottom: 8 }}>
                                                    {fillTemplate(c.foundingRateNote || 'Your founding rate of {rate}/month is yours to go back to.', tokens)}
                                                </p>
                                                <button type="button" onClick={() => { setFoundingError(''); setFoundingConfirm(true); }} style={outlineBtn}>
                                                    {fillTemplate(c.foundingBackLabel || 'Go back to {rate}/month when my year ends', tokens)}
                                                </button>
                                            </>
                                        )}
                                        {foundingError && <p style={{ ...quiet, color: 'var(--mom-danger, #e53e3e)', marginTop: 8 }}>{foundingError}</p>}
                                    </div>
                                );
                            })()}

                            {subscriptionCancelAt ? (
                                // M-3: Resume subscription — only shown when the subscription is pending
                                // cancellation. Clicking calls /api/resume-subscription which flips
                                // cancel_at_period_end back to false and clears classSubscriptionCancelAt.
                                <>
                                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', textAlign: 'center', lineHeight: 1.5, marginBottom: 12 }}>
                                        Your subscription is set to cancel. You'll have access until the end of your billing period.
                                    </p>
                                    {resumeError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #e53e3e)', marginBottom: 8, textAlign: 'center' }}>{resumeError}</p>}
                                    {resumeSuccess ? (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-charcoal)', textAlign: 'center', lineHeight: 1.5, fontWeight: 600 }}>
                                            Subscription resumed. Your billing will continue as normal.
                                        </p>
                                    ) : (
                                        <button disabled={resumeLoading} onClick={async () => {
                                            if (!window.confirm(`Resume your subscription? You'll continue to be billed ${displaySubscriptionPrice}.`)) return;
                                            setResumeLoading(true);
                                            setResumeError('');
                                            try {
                                                const token = await getToken();
                                                const resp = await fetch('/api/resume-subscription', {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                                    body: JSON.stringify({ userId: clerkUser?.id }),
                                                });
                                                if (!resp.ok) {
                                                    const data = await resp.json().catch(() => ({}));
                                                    throw new Error(data.error || 'Failed to resume subscription');
                                                }
                                                // Pull fresh metadata so the UI flips back to "active" without a manual refresh
                                                if (clerkUser?.reload) {
                                                    try { await clerkUser.reload(); } catch (_) { }
                                                }
                                                setResumeSuccess(true);
                                            } catch (e) {
                                                setResumeError(e.message || 'Something went wrong. Please try again.');
                                            }
                                            setResumeLoading(false);
                                        }} style={{
                                            display: 'block', margin: '0 auto', padding: '10px 28px', borderRadius: 50, fontSize: 13,
                                            fontFamily: 'var(--mom-font-sans)', fontWeight: 700, color: '#fff',
                                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                            border: 'none', cursor: 'pointer',
                                            boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 20%, transparent)',
                                            opacity: resumeLoading ? 0.5 : 1,
                                        }}>
                                            {resumeLoading ? 'Resuming...' : 'Resume Subscription'}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {cancelError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #e53e3e)', marginBottom: 8, textAlign: 'center' }}>{cancelError}</p>}
                                    {cancelSuccess ? (
                                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)', textAlign: 'center', lineHeight: 1.5 }}>
                                            Subscription cancelled.{' '}
                                            {cancelAt
                                                ? `You'll keep access until ${new Date(cancelAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`
                                                : "You'll keep access until the end of your billing period."}
                                        </p>
                                    ) : (
                                        <button disabled={cancelLoading} onClick={() => {
                                            // Open the on-brand confirmation modal. The actual cancel runs
                                            // from the modal's confirm button (doCancel) once a reason is picked.
                                            setCancelError('');
                                            setCancelReason('');
                                            setShowCancelModal(true);
                                        }} style={{
                                            display: 'block', margin: '0 auto', padding: '8px 24px', borderRadius: 50, fontSize: 12,
                                            fontFamily: 'var(--mom-font-sans)', fontWeight: 600, color: 'var(--mom-warm-gray)',
                                            background: 'none', border: '1px solid var(--mom-cream-dark)', cursor: 'pointer',
                                            opacity: cancelLoading ? 0.5 : 1,
                                        }}>
                                            {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* ── My Programs ───────────────────────────── */}
                <div className="slide-up" style={{ ...cardStyle, padding: '28px 28px', animationDelay: '0.1s' }}>
                    <div style={specHighlight} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 16 }}>{c.accountProgramsHeading || 'My Programs'}</h2>

                        {loading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[1, 2].map((i) => <div key={i} className="mom-skeleton" style={{ height: 56, borderRadius: 14 }} />)}
                            </div>
                        ) : purchasedPrograms.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {purchasedPrograms.map((prog) => (
                                    <button key={prog.id} className="mom-btn"
                                        onClick={() => navigate(`/program/${generateSlug(prog.name)}`)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 14,
                                            width: '100%', textAlign: 'left',
                                            padding: '14px 18px', borderRadius: 14,
                                            background: 'rgba(237,240,244,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                                            boxShadow: 'inset 0 2px 4px rgba(0,10,40,0.06), inset 0 1px 1px rgba(0,10,40,0.04), 0 1px 0 rgba(255,255,255,0.60)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                        }}>
                                        {prog.imageUrl ? (
                                            <div style={{
                                                width: 40, height: 40, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
                                                background: 'var(--mom-cream-dark)',
                                            }}>
                                                <img src={prog.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                                            </div>
                                        ) : (
                                            <div style={{
                                                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <span style={{ color: 'var(--mom-white)', fontFamily: 'var(--mom-font-serif)', fontWeight: 700, fontStyle: 'italic', fontSize: 16 }}>
                                                    {prog.name?.charAt(0) || 'P'}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 2 }}>{prog.name}</p>
                                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>
                                                {prog.programType === 'cohort' ? 'Cohort Program' : 'Self-Paced'}
                                            </p>
                                        </div>
                                        <span style={{ color: 'var(--mom-primary)', fontSize: 16, flexShrink: 0 }}>→</span>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="mom-inset" style={{ padding: '20px', textAlign: 'center' }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', marginBottom: 12 }}>{c.accountProgramsEmptyText || 'No programs yet'}</p>
                                <button className="mom-btn mom-btn-primary" onClick={() => navigate('/programs')}
                                    style={{ padding: '10px 28px', fontSize: 13, borderRadius: 50 }}>
                                    {c.accountProgramsBrowseLabel || 'Browse Programs'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Switch-to-yearly confirm (amount comes from Stripe's own preview) ── */}
                {switchOpen && (
                    <div
                        onClick={() => { if (!switchLoading) setSwitchOpen(false); }}
                        role="dialog" aria-modal="true" aria-label={planCopy.switchLabel}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 1000,
                            background: 'rgba(40,30,30,0.45)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 20, boxSizing: 'border-box',
                            animation: 'fadeIn 0.2s ease-out',
                        }}>
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                                borderRadius: 24, border: '1px solid rgba(255,255,255,0.6)',
                                boxShadow: 'var(--shadow-xl)',
                                width: '100%', maxWidth: 420, maxHeight: '88vh', overflowY: 'auto',
                                padding: '32px 28px', boxSizing: 'border-box',
                                animation: 'modalIn 0.25s ease-out',
                            }}>
                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 14, textAlign: 'center' }}>
                                {isFoundingMonthly ? (c.foundingLockLabel || 'Lock in a year') : planCopy.switchLabel}
                            </h2>

                            {!switchPreview && switchLoading && (
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', textAlign: 'center', marginBottom: 20 }}>
                                    Checking your price…
                                </p>
                            )}

                            {switchPreview && (() => {
                                const cur = switchPreview.currency;
                                const tokens = {
                                    today: formatMoney(switchPreview.amountDueToday, cur),
                                    price: formatMoney(switchPreview.annualAmount, cur),
                                    credit: formatMoney(switchPreview.credit, cur),
                                    date: new Date(switchPreview.renewsOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                                };
                                return (
                                    <div style={{ fontFamily: 'var(--mom-font-sans)', color: 'var(--mom-charcoal)', textAlign: 'center', lineHeight: 1.55, marginBottom: 22 }}>
                                        <p style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
                                            {fillTemplate(c.switchYearlyTodayTemplate || "You'll pay {today} today", tokens)}
                                        </p>
                                        {switchPreview.credit > 0 && (
                                            <p style={{ fontSize: 13, marginBottom: 10, opacity: 0.8 }}>
                                                {fillTemplate(c.switchYearlyCreditTemplate || "That's {price} less a {credit} credit for the unused part of this month.", tokens)}
                                            </p>
                                        )}
                                        <p style={{ fontSize: 13, opacity: 0.8 }}>
                                            {fillTemplate(c.switchYearlyBody || 'Then {price} every year, next on {date}. Cancel anytime — you keep access to the end of your year.', tokens)}
                                        </p>
                                        {switchPreview.founding && (
                                            <p style={{ fontSize: 13, marginTop: 10, fontWeight: 600 }}>
                                                {fillTemplate(c.foundingKeepRateNote || 'Your {rate}/month founding rate stays yours. When your year ends you can go back to it right here on this page.', { ...tokens, rate: formatMoney(switchPreview.foundingAmount, cur) })}
                                            </p>
                                        )}
                                    </div>
                                );
                            })()}

                            {switchError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-danger, #e53e3e)', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>{switchError}</p>}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {switchPreview && (
                                    <button type="button" disabled={switchLoading} onClick={confirmSwitch}
                                        style={{
                                            width: '100%', padding: '13px', borderRadius: 50, fontSize: 14,
                                            fontFamily: 'var(--mom-font-sans)', fontWeight: 700, color: '#fff',
                                            background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                            border: 'none', cursor: switchLoading ? 'default' : 'pointer',
                                            opacity: switchLoading ? 0.6 : 1,
                                        }}>
                                        {switchLoading ? 'Switching…' : (isFoundingMonthly ? (c.foundingLockLabel || 'Lock in a year') : planCopy.switchLabel)}
                                    </button>
                                )}
                                <button type="button" disabled={switchLoading} onClick={() => setSwitchOpen(false)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: 50, fontSize: 13,
                                        fontFamily: 'var(--mom-font-sans)', fontWeight: 600, color: 'var(--mom-charcoal)',
                                        background: 'none', border: '1px solid var(--mom-cream-dark)',
                                        cursor: switchLoading ? 'default' : 'pointer',
                                    }}>
                                    {c.switchYearlyCancelLabel || 'Not now'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Cancellation modal (on-brand replacement for window.confirm) ── */}
                {showCancelModal && (
                    <div
                        onClick={() => { if (!cancelLoading) setShowCancelModal(false); }}
                        role="dialog" aria-modal="true" aria-label={c.cancelModalHeading || 'Before you go'}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 1000,
                            background: 'rgba(40,30,30,0.45)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 20, boxSizing: 'border-box',
                            animation: 'fadeIn 0.2s ease-out',
                        }}>
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                                borderRadius: 24, border: '1px solid rgba(255,255,255,0.6)',
                                boxShadow: 'var(--shadow-xl)',
                                width: '100%', maxWidth: 420, maxHeight: '88vh', overflowY: 'auto',
                                padding: '32px 28px', boxSizing: 'border-box',
                                animation: 'modalIn 0.25s ease-out',
                            }}>
                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 24, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 8, textAlign: 'center' }}>
                                {c.cancelModalHeading || 'Before you go…'}
                            </h2>
                            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-warm-gray)', textAlign: 'center', lineHeight: 1.5, marginBottom: 22 }}>
                                {c.cancelModalSubtext || 'Mind sharing why? It helps Danielle make this better.'}
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                                {cancelReasons.map((r) => {
                                    const selected = cancelReason === r.key;
                                    return (
                                        <button key={r.key} type="button" onClick={() => setCancelReason(r.key)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 12,
                                                width: '100%', textAlign: 'left',
                                                padding: '14px 16px', borderRadius: 14, cursor: 'pointer',
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 14, lineHeight: 1.4,
                                                color: 'var(--mom-charcoal)',
                                                background: selected ? 'color-mix(in srgb, var(--mom-primary) 10%, transparent)' : 'rgba(255,255,255,0.6)',
                                                border: `1.5px solid ${selected ? 'var(--mom-primary)' : 'var(--mom-cream-dark)'}`,
                                                transition: 'background 0.15s, border-color 0.15s',
                                            }}>
                                            <span style={{
                                                flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
                                                border: `2px solid ${selected ? 'var(--mom-primary)' : 'var(--mom-cream-dark)'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'border-color 0.15s',
                                            }}>
                                                {selected && <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--mom-primary)' }} />}
                                            </span>
                                            <span>{r.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {cancelError && <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-danger, #e53e3e)', marginBottom: 12, textAlign: 'center' }}>{cancelError}</p>}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {/* Primary = retention. Keeping the membership is the emphasized action. */}
                                <button type="button" disabled={cancelLoading} onClick={() => setShowCancelModal(false)}
                                    style={{
                                        width: '100%', padding: '13px', borderRadius: 50, fontSize: 14,
                                        fontFamily: 'var(--mom-font-sans)', fontWeight: 700, color: '#fff',
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        border: 'none', cursor: cancelLoading ? 'default' : 'pointer',
                                        boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 20%, transparent)',
                                        opacity: cancelLoading ? 0.6 : 1,
                                    }}>
                                    {c.cancelKeepLabel || 'Keep my membership'}
                                </button>
                                {/* Destructive confirm — disabled until a reason is selected (required). */}
                                <button type="button" disabled={cancelLoading || !cancelReason} onClick={doCancel}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: 50, fontSize: 13,
                                        fontFamily: 'var(--mom-font-sans)', fontWeight: 600, color: 'var(--mom-warm-gray)',
                                        // Once a reason is picked, a thin brand-pink border signals the
                                        // button is now actionable (without shouting). Muted cream while disabled.
                                        background: 'none', border: `1px solid ${cancelReason ? 'var(--mom-primary)' : 'var(--mom-cream-dark)'}`,
                                        cursor: (cancelLoading || !cancelReason) ? 'default' : 'pointer',
                                        opacity: (cancelLoading || !cancelReason) ? 0.5 : 1,
                                    }}>
                                    {cancelLoading ? 'Cancelling…' : (c.cancelConfirmLabel || 'Cancel membership')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Contact Danielle ──────────────────────── */}
                <div className="slide-up" style={{ ...cardStyle, padding: '28px 28px', animationDelay: '0.16s' }}>
                    <div style={specHighlight} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 6 }}>{c.contactHeading || 'Contact Danielle'}</h2>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', marginBottom: 16, lineHeight: 1.5 }}>
                            {c.contactDescription || 'Questions about a program, need help with your account, or just want to say hi?'}
                        </p>

                        <a href={`mailto:${c.contactEmail || 'hello@daniellenbarr.com'}`} style={{
                            display: 'flex', alignItems: 'center', gap: 14,
                            width: '100%', textAlign: 'left',
                            padding: '14px 18px', borderRadius: 14,
                            background: 'rgba(237,240,244,0.6)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                            boxShadow: 'inset 0 2px 4px rgba(0,10,40,0.06), inset 0 1px 1px rgba(0,10,40,0.04), 0 1px 0 rgba(255,255,255,0.60)',
                            textDecoration: 'none',
                            transition: 'all 0.15s ease',
                        }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 20%, transparent)',
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="4" width="20" height="16" rx="3" />
                                    <path d="M22 7l-10 6L2 7" />
                                </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 2 }}>{c.contactEmail || 'hello@daniellenbarr.com'}</p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>{c.contactButtonLabel || 'Tap to send an email'}</p>
                            </div>
                            <span style={{ color: 'var(--mom-primary)', fontSize: 16, flexShrink: 0 }}>↗</span>
                        </a>
                    </div>
                </div>

                {/* ── Notifications ────────────────────────── */}
                {!demoMode && (
                    <div className="slide-up" style={{ ...cardStyle, padding: '28px 28px', animationDelay: '0.2s' }}>
                        <div style={specHighlight} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)', marginBottom: 14 }}>Notifications</h2>
                            <PushNotificationToggle userId={clerkUser?.id || ''} />
                        </div>
                    </div>
                )}

                {/* ── Add to Home Screen ─────────────────────── */}
                <div className="slide-up" style={{ animationDelay: '0.24s' }}>
                    <HomeScreenCard cardStyle={cardStyle} specHighlight={specHighlight} />
                </div>

                {/* ── Sign Out ──────────────────────────────── */}
                <div className="slide-up" style={{ textAlign: 'center', paddingTop: 8, animationDelay: '0.28s' }}>
                    <button className="mom-btn mom-btn-outline" onClick={signOut}
                        style={{ padding: '12px 40px', fontSize: 14, borderRadius: 50, width: '100%', maxWidth: 240 }}>
                        Sign Out
                    </button>
                </div>

                {/* ── Legal link ────────────────────────────────
                    Quiet, low-emphasis link to the public /terms page so
                    signed-in users can revisit what they agreed to at signup.
                    The primary discovery surface is Clerk's hosted signup form
                    (which links here directly). This is the in-app revisit
                    affordance — see `docs/ux/waiver-clerk-express-consent.md`. */}
                <div className="slide-up" style={{ textAlign: 'center', paddingTop: 4, paddingBottom: 8, animationDelay: '0.32s' }}>
                    <button onClick={() => navigate('/terms')}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                            color: 'var(--mom-warm-gray)', padding: '8px 16px',
                            textDecoration: 'underline', textDecorationColor: 'var(--mom-cream-dark)',
                            textUnderlineOffset: 3,
                        }}>
                        Terms & Privacy
                    </button>
                </div>

            </div>
        </div>
    );
}

export default Account;